import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import PasswordField from 'components/Common/LabelledTextField/PasswordField'; // Adjust the import based on your actual project structure
import { getLocalStorageItem } from 'hooks';
import { useAuthMe } from 'queries/AuthQueries';
import { useDeletePatientAcconutApi } from 'queries/Patient';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
}

interface FormFields {
  currentPassword: string;
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormFields>();
  const { data: profileData } = useAuthMe();
  const [showTimer, setShowTimer] = useState(false);
  const [timer, setTimer] = useState(10);
  const [passError, setpassError] = useState(null);

  const UserDetails = getLocalStorageItem('roles');
  const headerKeys = {
    'x-klynex-access-key':
      profileData?.data?.account?.patientsAccessKey ||
      UserDetails?.account?.patientsAccessKey,
  };
  const { mutate: deletePatients } = useDeletePatientAcconutApi(headerKeys);
  const currentPassword = watch('currentPassword');

  const handleDeactivateUser = (data: FormFields) => {
    const payload = {
      accountId: profileData?.data?.account?.id,
      password: data?.currentPassword,
    };
    deletePatients(payload as any, {
      onSuccess: () => {
        setShowTimer(true);
        setpassError(null);
      },
      onError: (err: any) => {
        setpassError(err.fieldErrors[0].message);
      },
    });
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (showTimer && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      navigate('/');
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showTimer, timer, navigate]);

  return (
    <Dialog
      disableEscapeKeyDown
      open={open}
      aria-labelledby="deactivate-account-dialog-title"
      aria-describedby="deactivate-account-dialog-description"
      maxWidth="sm"
    >
      <div>
        <hr className="mt-0 border-t-4 border-solid border-red-500 pt-0" />
        <DeleteIcon className="m-auto flex items-center justify-center rounded-full bg-red-500 p-2 text-center text-[50px] text-white" />
      </div>
      <div className="max-w-[550px] rounded-lg px-4">
        <DialogTitle
          id="deactivate-account-dialog-title"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            textAlign: 'center',
            color: '#333',
          }}
        >
          {showTimer ? t('accountDeleted') : t('deleteAccount')}
        </DialogTitle>
        <DialogContent sx={{ marginTop: '2px' }}>
          {showTimer ? (
            <Typography className="text-center text-sm">
              {t('accountDeletedMessage')}
            </Typography>
          ) : (
            <>
              <Typography
                sx={{ fontSize: '1rem', color: '#666' }}
                className="text-justify"
              >
                {t('confirmAccountDelete')}
              </Typography>
              <form onSubmit={handleSubmit(handleDeactivateUser)}>
                <Typography className="mt-4 text-sm font-semibold">
                  {t('verifyPass')}
                </Typography>
                <PasswordField
                  {...register('currentPassword', { required: true })}
                  error={Boolean(errors.currentPassword)}
                  helperText={
                    errors.currentPassword ? errors.currentPassword.message : ''
                  }
                />
                <Typography className="mt-1 text-sm text-red-500 ">
                  {passError}
                </Typography>
                <DialogActions
                  sx={{ justifyContent: 'center', padding: '20px 24px' }}
                >
                  {!showTimer && (
                    <>
                      <Button
                        onClick={onClose}
                        sx={{
                          fontWeight: 'bold',
                          textTransform: 'none',
                          borderRadius: '20px',
                          //  width: '150px',
                        }}
                        variant="outlined"
                        color="primary"
                      >
                        {t('noCancelIt')}
                      </Button>
                      <Button
                        type="submit"
                        sx={{
                          fontWeight: 'bold',
                          textTransform: 'none',
                          borderRadius: '20px',
                          // width: '150px',
                        }}
                        variant="contained"
                        color="error"
                        autoFocus
                        disabled={!currentPassword}
                      >
                        {t('yesDelete')}
                      </Button>
                    </>
                  )}
                </DialogActions>
              </form>
            </>
          )}
        </DialogContent>
      </div>
    </Dialog>
  );
};

export default DeleteAccountDialog;
